import * as React from "react";

/**
 * Theme selection. **Two states, and light is the default.**
 *
 * There used to be a third, "system", which deferred to `prefers-color-scheme`
 * when the user had not chosen. It is gone deliberately. This product's design
 * direction is light-first - field technicians work in direct sunlight, where a
 * dark UI loses to screen reflection and constricted pupils - so an unconfigured
 * account should land on light regardless of what the laptop's OS is set to.
 * With "system" in place, every developer on a dark-mode machine saw a dark
 * console by default and the light theme went untested for weeks at a time.
 *
 * That makes the pre-paint script in index.html load-bearing rather than a
 * nicety. `tokens.css` still carries a `@media (prefers-color-scheme: dark)`
 * block guarded as `:root:not([data-theme="light"])`, so **an unstamped document
 * on a dark-mode OS renders dark**. Light is only the default because the script
 * writes `data-theme="light"` before the first paint when storage is empty.
 * Remove that line and the default silently becomes "whatever the OS says"
 * again, which is the exact behaviour this file no longer wants.
 */

export type ThemeChoice = "light" | "dark";

const STORAGE_KEY = "taskiq.theme";
const DEFAULT: ThemeChoice = "light";

interface ThemeApi {
  theme: ThemeChoice;
  setTheme: (choice: ThemeChoice) => void;
  toggle: () => void;
}

const ThemeContext = React.createContext<ThemeApi | null>(null);

function readStored(): ThemeChoice {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === "dark" || stored === "light" ? stored : DEFAULT;
  } catch {
    // Private mode, or storage disabled by policy. The choice still applies for
    // this session; it just will not survive a reload.
    return DEFAULT;
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = React.useState<ThemeChoice>(readStored);

  const setTheme = React.useCallback((next: ThemeChoice) => {
    setThemeState(next);
    document.documentElement.dataset.theme = next;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* see readStored */
    }
  }, []);

  // The script in index.html already stamped the attribute before React booted.
  // This only matters when it could not - storage threw, or the document was
  // rendered by something that did not run the head script at all.
  React.useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  const value = React.useMemo<ThemeApi>(
    () => ({ theme, setTheme, toggle: () => setTheme(theme === "dark" ? "light" : "dark") }),
    [theme, setTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeApi {
  const context = React.useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used inside <ThemeProvider>");
  return context;
}
